/**
 * Discovery / Honey-PII - Phase 2 part 2 wiring.
 * Sources:
 *   GET    /v1/discovery/honey-pii/canaries
 *   POST   /v1/discovery/honey-pii/canaries
 *   DELETE /v1/discovery/honey-pii/canaries/:id
 */
import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Bug, Plus, AlertTriangle, MapPin, Eye, Loader2, ShieldCheck, RotateCw, Zap, Search } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listHoneyCanaries,
  plantHoneyCanary,
  retireHoneyCanary,
  triggerHoneyCanary,
  type HoneyCanary,
} from '@/lib/api';

export function DiscoveryHoneyPii() {
  const tx = useRegulationText();
  const navigate = useNavigate();
  const [canaries, setCanaries] = useState<HoneyCanary[]>([]);
  const [counts, setCounts] = useState<{ total: number; active: number; triggered: number; retired: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planting, setPlanting] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'retire' | 'rotate' | 'trigger' | null>(null);
  const [lastTrigger, setLastTrigger] = useState<{ findingId: string } | null>(null);
  const [plantForm, setPlantForm] = useState({
    canaryType: 'aadhaar' as HoneyCanary['canaryType'],
    plantedInAssetId: '',
    plantedValueHash: '',
  });

  const refresh = async () => {
    try {
      const r = await listHoneyCanaries();
      setCanaries(r.canaries);
      setCounts(r.counts ?? null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const onPlant = async () => {
    if (!plantForm.plantedInAssetId || !plantForm.plantedValueHash) return;
    setPlanting(true);
    try {
      await plantHoneyCanary({
        canaryType: plantForm.canaryType,
        plantedInAssetId: plantForm.plantedInAssetId,
        plantedValueHash: plantForm.plantedValueHash,
      });
      setPlantForm({ canaryType: 'aadhaar', plantedInAssetId: '', plantedValueHash: '' });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'plant failed');
    } finally {
      setPlanting(false);
    }
  };

  const onRetire = async (id: string) => {
    setPendingId(id);
    setPendingAction('retire');
    try {
      await retireHoneyCanary(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'retire failed');
    } finally {
      setPendingId(null);
      setPendingAction(null);
    }
  };

  /**
   * Rotate = retire the current canary and immediately plant a fresh
   * one of the same type in the same asset. Useful when the value-
   * hash may have been disclosed (e.g. after a triage), so the same
   * trap is still in place but the old hash is dead.
   */
  const onRotate = async (c: HoneyCanary) => {
    setPendingId(c.id);
    setPendingAction('rotate');
    try {
      await retireHoneyCanary(c.id);
      // Generate a fresh hash from the existing one + timestamp so the
      // rotated canary is unambiguously different.
      const fresh = `${c.plantedValueHash}_r${Date.now().toString(36)}`.slice(0, 64);
      await plantHoneyCanary({
        canaryType: c.canaryType,
        plantedInAssetId: c.plantedInAssetId,
        ...(c.plantedInColumnId ? { plantedInColumnId: c.plantedInColumnId } : {}),
        plantedValueHash: fresh,
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'rotate failed');
    } finally {
      setPendingId(null);
      setPendingAction(null);
    }
  };

  /**
   * Trigger = manually fire the tripwire (records a honey_pii_read
   * finding + marks the canary triggered). Used to verify the
   * SIEM / playbook integration end-to-end without waiting for a
   * real unauthorised read. Calls POST /honey-pii/canaries/:id/trigger.
   */
  const onTrigger = async (id: string) => {
    setPendingId(id);
    setPendingAction('trigger');
    setLastTrigger(null);
    try {
      const res = await triggerHoneyCanary(id);
      setLastTrigger({ findingId: res.finding.id });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'trigger failed');
    } finally {
      setPendingId(null);
      setPendingAction(null);
    }
  };

  /**
   * Investigate = navigate to Findings filtered to this canary's
   * triggered findings (or empty if the canary hasn't fired yet).
   */
  const onInvestigate = (c: HoneyCanary) => {
    navigate({ to: '/dspm/findings', search: { canary: c.id } as never });
  };

  const active = canaries.filter((c) => c.status === 'active');
  const triggered = canaries.filter((c) => c.triggeredAt);

  return (
    <PageContainer>
      <PageHeader
        title={tx("Honey-PII Canaries")}
        subtitle="Tripwire records planted in production data. Any read fires a high-severity finding. The actor that read it is, by construction, doing something they should not be."
        badges={[
          { label: `${active.length} active`, variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
      />

      {loading && (
        <LoadingState caption="Loading canaries…" />
      )}
      {error && !loading && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs">
          <span className="font-medium">Error:</span> {error}
          <Button size="sm" variant="glass" className="ml-2" onClick={() => { setError(null); setLoading(true); refresh().finally(() => setLoading(false)); }}>
            Retry
          </Button>
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Counted by the server over every canary (release 2.1 item 4), not the 200 listed. */}
            <KpiCard label="Active canaries" value={counts?.active ?? active.length} icon={Bug} hint="planted + waiting" />
            <KpiCard label="Triggered" value={counts?.triggered ?? triggered.length} icon={AlertTriangle} glow={(counts?.triggered ?? triggered.length) > 0 ? 'critical' : 'low'} hint="any read = breach" />
            <KpiCard label="Retired" value={counts?.retired ?? canaries.length - active.length - triggered.length} icon={ShieldCheck} hint="lifecycle complete" />
            <KpiCard label="Total" value={counts?.total ?? canaries.length} icon={Eye} />
          </div>

          {/* Plant new canary */}
          <GlassCard className="p-5 mb-6">
            <CardHeader className="p-0 mb-3">
              <CardTitle>Plant a new canary</CardTitle>
              <CardDescription clamp>Choose canary type, target asset, and a value-hash (so the live tripwire stays one-way).</CardDescription>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select
                value={plantForm.canaryType}
                onChange={(e) => setPlantForm({ ...plantForm, canaryType: e.target.value as HoneyCanary['canaryType'] })}
                className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40"
              >
                <option value="aadhaar">aadhaar</option>
                <option value="pan">pan</option>
                <option value="email">email</option>
                <option value="phone">phone</option>
                <option value="identity_bundle">identity_bundle</option>
              </select>
              <input
                placeholder="planted-in asset id (UUID)"
                value={plantForm.plantedInAssetId}
                onChange={(e) => setPlantForm({ ...plantForm, plantedInAssetId: e.target.value })}
                className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40 font-mono text-xs"
              />
              <input
                placeholder="planted value hash (SHA-256)"
                value={plantForm.plantedValueHash}
                onChange={(e) => setPlantForm({ ...plantForm, plantedValueHash: e.target.value })}
                className="glass-lite rounded-md px-3 py-2 text-sm bg-transparent border border-border/40 font-mono text-xs"
              />
              <Button onClick={onPlant} disabled={planting || !plantForm.plantedInAssetId || !plantForm.plantedValueHash} data-action="disc.dspm_honey.a01">
                {planting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Plant canary
              </Button>
            </div>
          </GlassCard>

          {/* Canary list */}
          {canaries.length === 0 ? (
            <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
              <Bug className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground/90">No canaries planted yet</div>
              <div className="mt-1"><Rx>Plant your first canary above to start the honey-PII tripwire programme.</Rx></div>
            </div>
          ) : (
            <>
            {lastTrigger && (
              <div className="glass-lite border border-success/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
                <Zap className="h-4 w-4 text-success shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Canary triggered</div>
                  <div className="text-muted-foreground mt-0.5">
                    Finding <code className="font-mono">{lastTrigger.findingId.slice(0, 18)}…</code> recorded. SIEM + playbook routing fires from this point in the canonical findings pipeline.
                  </div>
                </div>
                <Button size="sm" variant="glass" onClick={() => setLastTrigger(null)}>Dismiss</Button>
              </div>
            )}
            <GlassCard className="p-0 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Planted in</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Planted at</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              {canaries.map((c, i) => {
                const pending = pendingId === c.id;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors"
                  >
                    <div className="col-span-2">
                      <Badge variant="glass" className="text-[10px] font-mono">{c.canaryType}</Badge>
                    </div>
                    <div className="col-span-3 font-mono text-xs flex items-center gap-1.5 truncate">
                      <MapPin className="h-3 w-3 opacity-50" />
                      <code className="truncate">{c.plantedInAssetId.slice(0, 18)}…</code>
                    </div>
                    <div className="col-span-2">
                      {c.triggeredAt ? (
                        <Badge variant="critical" className="text-[9px]">TRIGGERED</Badge>
                      ) : c.status === 'active' ? (
                        <Badge variant="success" className="text-[9px]">Active</Badge>
                      ) : (
                        <Badge variant="glass" className="text-[9px]">{c.status}</Badge>
                      )}
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground">
                      {new Date(c.plantedAt).toLocaleDateString()}
                    </div>
                    <div className="col-span-3 text-right flex items-center justify-end gap-1.5 flex-wrap">
                      <Button
                        size="sm"
                        variant="glass"
                        onClick={() => onInvestigate(c)}
                        title="Open Findings filtered to this canary's history"
                      >
                        <Search className="h-3 w-3" /> Investigate
                      </Button>
                      {c.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            variant="glass"
                            onClick={() => onTrigger(c.id)}
                            disabled={pending}
                            title="Fire the tripwire manually (records honey_pii_read finding)"
                            data-action="disc.dspm_honey.a05"
                          >
                            {pending && pendingAction === 'trigger' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                            Trigger
                          </Button>
                          <Button
                            size="sm"
                            variant="glass"
                            onClick={() => onRotate(c)}
                            disabled={pending}
                            title="Retire + re-plant with a fresh hash"
                            data-action="disc.dspm_honey.a03 disc.dspm_honey.a04"
                          >
                            {pending && pendingAction === 'rotate' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                            Rotate
                          </Button>
                          <Button
                            size="sm"
                            variant="glass"
                            onClick={() => onRetire(c.id)}
                            disabled={pending}
                            data-action="disc.dspm_honey.a02"
                          >
                            {pending && pendingAction === 'retire' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Retire
                          </Button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </GlassCard>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}
